import React, { useRef, useState, useEffect, useMemo } from "react";
import { X, Printer, Calendar, History, RotateCcw, Lock, Unlock, AlertCircle, Send } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/axios";
import authorisedSignature from "../../assets/authorised-signature.png";
import SalarySlipDocument from "./SalarySlipDocument";
import {
    getCurrentPayPeriod,
    getRecentPayPeriodOptions,
    getPayPeriodMonthBounds,
    getLastNPeriodsFromKey,
    listPeriodsBetweenKeys,
    filterExpensesInPeriod,
    computeSlipAmounts,
    computePeriodAttendanceStats,
    getAttendanceQueryBounds,
    getProratedSalaryForPeriod,
    formatPeriodRangeLabel,
    defaultCustomRangeKeys,
    clampCustomRangeKeys,
} from "../../utils/salarySlipUtils";
import { getMinimumWordsError } from "../../utils/textValidation";

const PAYSLIP_ISSUE_MIN_WORDS = 3;

const RANGE_MODES = [
    { id: "single", label: "This month only" },
    { id: "3", label: "Last 3 months" },
    { id: "6", label: "Last 6 months" },
    { id: "custom", label: "Custom range" },
];

const SalarySlipModal = ({ open, onClose, employee: propEmployee }) => {
    const { employee: contextEmployee, fetchEmployee } = useAuth();
    const employee = propEmployee || contextEmployee;
    const printAreaRef = useRef();
    const wasOpenRef = useRef(false);
    const [companySettings, setCompanySettings] = useState(null);

    const monthBounds = useMemo(() => getPayPeriodMonthBounds(36, companySettings?.payCycle), [companySettings?.payCycle]);
    const monthOptions = useMemo(() => getRecentPayPeriodOptions(36, companySettings?.payCycle), [companySettings?.payCycle]);
    const defaultKey = getCurrentPayPeriod(companySettings?.payCycle).key;
    const currentKey = defaultKey;

    const [approvedExpenses, setApprovedExpenses] = useState([]);
    const [selectedMonthKey, setSelectedMonthKey] = useState(defaultKey);
    const [rangeMode, setRangeMode] = useState("single");
    const [customFromKey, setCustomFromKey] = useState(defaultKey);
    const [customToKey, setCustomToKey] = useState(defaultKey);
    const [savedSlips, setSavedSlips] = useState([]);
    const [salaryHistory, setSalaryHistory] = useState([]);
    const [attendanceRecords, setAttendanceRecords] = useState([]);
    const [leaveRecords, setLeaveRecords] = useState([]);
    const [activeSavedSlips, setActiveSavedSlips] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [savingHistory, setSavingHistory] = useState(false);
    const [payslipIssues, setPayslipIssues] = useState([]);
    const [issueOpen, setIssueOpen] = useState(false);
    const [issueForm, setIssueForm] = useState({ issueType: "salary", description: "" });
    const [issueMessage, setIssueMessage] = useState("");
    const [issueSaving, setIssueSaving] = useState(false);

    useEffect(() => {
        if (!open) {
            setApprovedExpenses([]);
            setSavedSlips([]);
            setSalaryHistory([]);
            setAttendanceRecords([]);
            setLeaveRecords([]);
            setActiveSavedSlips(null);
            return;
        }
        setHistoryLoading(true);
        let cancelled = false;
        (async () => {
            try {
                await fetchEmployee?.();
                api.get("/api/settings").then(res => setCompanySettings(res.data)).catch(console.error);
            } catch {
                /* ignore */
            }
            try {
                const res = await api.get("/api/employee/expenses/my");
                if (!cancelled) {
                    setApprovedExpenses(res.data.filter((e) => e.status === "Approved"));
                }
            } catch {
                if (!cancelled) setApprovedExpenses([]);
            }
            try {
                const res = await api.get("/api/employee/salary-slips");
                if (!cancelled) setSavedSlips(Array.isArray(res.data) ? res.data : []);
            } catch {
                if (!cancelled) setSavedSlips([]);
            }
            try {
                const res = await api.get("/api/employee/salary-history");
                if (!cancelled) setSalaryHistory(Array.isArray(res.data) ? res.data : []);
            } catch {
                if (!cancelled) setSalaryHistory([]);
            }
            try {
                const res = await api.get("/api/employee/payslip-issues");
                if (!cancelled) setPayslipIssues(Array.isArray(res.data) ? res.data : []);
            } catch {
                if (!cancelled) setPayslipIssues([]);
            } finally {
                if (!cancelled) setHistoryLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (open && !wasOpenRef.current) {
            setSelectedMonthKey(defaultKey);
            setRangeMode("single");
            const { fromKey, toKey } = defaultCustomRangeKeys(defaultKey, 3, monthBounds);
            setCustomFromKey(fromKey);
            setCustomToKey(toKey);
        }
        wasOpenRef.current = open;
    }, [open, defaultKey, monthBounds]);

    const handleRangeModeChange = (mode) => {
        setActiveSavedSlips(null);
        setRangeMode(mode);
        if (mode === "custom") {
            const { fromKey, toKey } = defaultCustomRangeKeys(
                selectedMonthKey || defaultKey,
                3,
                monthBounds,
            );
            setCustomFromKey(fromKey);
            setCustomToKey(toKey);
        }
    };

    const handleCustomFromChange = (fromKey) => {
        setActiveSavedSlips(null);
        const { fromKey: from, toKey: to } = clampCustomRangeKeys(fromKey, customToKey, monthBounds);
        setCustomFromKey(from);
        setCustomToKey(to);
    };

    const handleCustomToChange = (toKey) => {
        setActiveSavedSlips(null);
        const { fromKey: from, toKey: to } = clampCustomRangeKeys(customFromKey, toKey, monthBounds);
        setCustomFromKey(from);
        setCustomToKey(to);
    };

    const periods = useMemo(() => {
        if (rangeMode === "single") {
            return getLastNPeriodsFromKey(selectedMonthKey, 1);
        }
        if (rangeMode === "3") {
            return getLastNPeriodsFromKey(selectedMonthKey, 3);
        }
        if (rangeMode === "6") {
            return getLastNPeriodsFromKey(selectedMonthKey, 6);
        }
        return listPeriodsBetweenKeys(customFromKey, customToKey);
    }, [rangeMode, selectedMonthKey, customFromKey, customToKey]);

    useEffect(() => {
        if (!open || !periods.length) return;
        const bounds = getAttendanceQueryBounds(periods);
        if (!bounds) return;

        let cancelled = false;
        Promise.all([
            api.get("/api/employee/attendance/my", {
                params: { ...bounds, limit: 400, _t: Date.now() },
            }),
            api.get("/api/employee/leave/my", {
                params: { limit: 60, _t: Date.now() },
            }),
        ]).then(([attendanceRes, leaveRes]) => {
            if (cancelled) return;
            setAttendanceRecords(Array.isArray(attendanceRes.data) ? attendanceRes.data : []);
            setLeaveRecords(Array.isArray(leaveRes.data) ? leaveRes.data : []);
        }).catch(() => {
            if (cancelled) return;
            setAttendanceRecords([]);
            setLeaveRecords([]);
        });

        return () => { cancelled = true; };
    }, [open, periods]);

    const slipsData = useMemo(
        () =>
            periods.map((period) => {
                const periodExpenses = filterExpensesInPeriod(
                    approvedExpenses,
                    period.periodStart,
                    period.periodEnd,
                );
                const proratedSalary = getProratedSalaryForPeriod(employee, salaryHistory, period);
                const fullMonthlySalary = Number(employee?.salary || 0);
                const salaryEmployee = { ...employee, salary: fullMonthlySalary };
                const attendanceStats = computePeriodAttendanceStats(salaryEmployee, period, {
                    attendance: attendanceRecords,
                    leaves: leaveRecords,
                });
                const amounts = computeSlipAmounts(
                    salaryEmployee,
                    { ...period, grossOverride: fullMonthlySalary },
                    periodExpenses,
                    period.key === currentKey,
                    attendanceStats,
                );
                return {
                    period: { ...period, salarySegments: proratedSalary.segments, ...amounts },
                    expenses: periodExpenses,
                    employee: salaryEmployee,
                };
            }),
        [periods, approvedExpenses, employee, salaryHistory, currentKey, attendanceRecords, leaveRecords],
    );

    const displaySlipsData = activeSavedSlips || slipsData;

    if (!open || !employee) return null;

    const signatureSrc =
        typeof authorisedSignature === "string" && /^https?:\/\//i.test(authorisedSignature)
            ? authorisedSignature
            : `${window.location.origin}${authorisedSignature}`;

    const displayPeriods = displaySlipsData.map(({ period }) => period);
    const rangeLabel = formatPeriodRangeLabel(displayPeriods);
    const slipCount = displaySlipsData.length;
    const activePeriod = displayPeriods[0] || {};
    const activeIssue = payslipIssues.find((issue) => issue.periodKey === activePeriod.key && issue.status === "open");

    const handleMonthChange = (value) => {
        setActiveSavedSlips(null);
        setSelectedMonthKey(value);
    };

    const saveGeneratedSlips = async () => {
        if (activeSavedSlips) return savedSlips;
        setSavingHistory(true);
        try {
            const res = await api.post("/api/employee/salary-slips", {
                slips: slipsData.map(({ period, expenses }) => ({ period, expenses })),
            });
            const next = Array.isArray(res.data) ? res.data : [];
            setSavedSlips((current) => {
                const byKey = new Map(current.map((item) => [item.periodKey, item]));
                next.forEach((item) => byKey.set(item.periodKey, item));
                return Array.from(byKey.values()).sort((a, b) => String(b.periodKey).localeCompare(String(a.periodKey)));
            });
            return next;
        } finally {
            setSavingHistory(false);
        }
    };

    const loadSavedSlip = (slip) => {
        if (!slip?.snapshot) return;
        setActiveSavedSlips([{
            period: slip.snapshot.period,
            expenses: slip.snapshot.expenses || [],
            employee: slip.snapshot.employee,
        }]);
    };

    const handlePrint = async () => {
        await saveGeneratedSlips();
        const pw = window.open("", "_blank", "height=900,width=1050");
        const title =
            slipCount > 1
                ? `Salary Slips – ${employee.name} – ${rangeLabel}`
                : `Salary Slip – ${employee.name} – ${displayPeriods[0]?.payMonth || ""}`;

        pw.document.write(`<html><head><title>${title}</title>
        <style>
            *{box-sizing:border-box;margin:0;padding:0}
            body{font-family:'Inter',Arial,sans-serif;font-size:11px;color:#111;background:#fff;padding:20px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
            .slip{max-width:860px;margin:0 auto}
            .slip-page{page-break-after:always;break-after:page}
            .slip-page:last-child{page-break-after:auto;break-after:auto}
        </style></head><body><div class="slip">`);
        pw.document.write(printAreaRef.current.innerHTML);
        pw.document.write("</div></body></html>");
        pw.document.close();
        setTimeout(() => {
            pw.focus();
            pw.print();
            pw.close();
        }, 400);
    };

    const submitPayslipIssue = async (event) => {
        event.preventDefault();
        const descriptionError = getMinimumWordsError(issueForm.description, "Payslip issue description", PAYSLIP_ISSUE_MIN_WORDS);
        if (descriptionError) {
            setIssueMessage(descriptionError);
            return;
        }

        setIssueSaving(true);
        setIssueMessage("");
        try {
            const res = await api.post("/api/employee/payslip-issues", {
                periodKey: activePeriod.key || selectedMonthKey,
                payMonth: activePeriod.payMonth || "",
                issueType: issueForm.issueType,
                description: issueForm.description,
            });
            setPayslipIssues((current) => [res.data, ...current]);
            setIssueForm({ issueType: "salary", description: "" });
            setIssueOpen(false);
        } catch (err) {
            setIssueMessage(err.response?.data?.message || "Failed to report payslip issue.");
        } finally {
            setIssueSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[200] flex justify-center items-start px-2 py-6 overflow-y-auto">
            <div onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <div
                className="relative w-full max-w-4xl bg-white shadow-2xl z-10 mt-2 mb-10 rounded-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="flex flex-col gap-3 px-5 py-3 border-b border-gray-200 bg-gray-50 relative z-20">
                    <div className="flex justify-end items-center">
                        <div className="hidden">
                            <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">Salary Slip</h2>
                            <p className="text-[10px] text-gray-400 mt-0.5">
                                {slipCount > 1
                                    ? `${slipCount} slips · ${rangeLabel}`
                                    : "Select pay period · Print or save as PDF"}
                            </p>
                        </div>
                        <div className="flex gap-2">
                            {activeSavedSlips && (
                                <button
                                    type="button"
                                    onClick={() => setActiveSavedSlips(null)}
                                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 hover:bg-gray-100 text-gray-600 font-semibold text-xs rounded-lg transition-all"
                                >
                                    <RotateCcw size={13} />
                                    Current
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={handlePrint}
                                disabled={savingHistory}
                                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#1e3a8a] hover:bg-[#1e40af] text-white font-semibold text-xs rounded-lg transition-all"
                            >
                                <Printer size={13} />
                                {savingHistory ? "Saving..." : slipCount > 1 ? `Download ${slipCount} slips` : "Print / Save PDF"}
                            </button>
                            <button
                                type="button"
                                onClick={() => setIssueOpen(true)}
                                className="inline-flex items-center gap-1.5 px-3 py-2 border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700 font-semibold text-xs rounded-lg transition-all"
                            >
                                <AlertCircle size={13} />
                                {activeIssue ? "Issue Open" : "Report Issue"}
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                className="w-9 h-9 flex items-center justify-center border border-gray-300 hover:bg-gray-100 text-gray-500 rounded-lg transition-all"
                            >
                                <X size={18} />
                            </button>
                        </div>
                    </div>

                    <div className="flex flex-col gap-3 pt-1 border-t border-gray-200/80">
                        <div className="flex flex-wrap items-end gap-3">
                            {rangeMode !== "custom" && (
                                <div className="flex flex-col gap-1 min-w-[180px]">
                                    <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                                        <Calendar size={11} /> Pay month
                                    </label>
                                    <select
                                        value={selectedMonthKey}
                                        onChange={(e) => handleMonthChange(e.target.value)}
                                        className="text-xs border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-800 focus:border-[#1e3a8a] outline-none cursor-pointer"
                                    >
                                        {monthOptions.map((o) => (
                                            <option key={o.value} value={o.value}>
                                                {o.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
                                <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                                    Download period
                                </label>
                                <div className="flex flex-wrap gap-1.5">
                                    {RANGE_MODES.map((m) => (
                                        <button
                                            key={m.id}
                                            type="button"
                                            onClick={() => handleRangeModeChange(m.id)}
                                            className={`text-[11px] px-3 py-1.5 rounded-lg border font-medium transition-all ${
                                                rangeMode === m.id
                                                    ? "bg-[#1e3a8a] text-white border-[#1e3a8a]"
                                                    : "bg-white text-gray-600 border-gray-300 hover:border-[#1e3a8a]"
                                            }`}
                                        >
                                            {m.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {rangeMode === "custom" && (
                            <div className="w-full flex flex-col sm:flex-row gap-3 p-3 rounded-xl bg-white border border-[#1e3a8a]/20">
                                <p className="text-[10px] text-gray-500 w-full sm:hidden">
                                    Pick any months (up to 36 months back). Each month generates one salary slip.
                                </p>
                                <div className="flex flex-col gap-1 flex-1 min-w-[160px]">
                                    <label htmlFor="emp-slip-from-month" className="text-[10px] font-semibold text-gray-600">
                                        From (start month)
                                    </label>
                                    <input
                                        id="emp-slip-from-month"
                                        type="month"
                                        min={monthBounds.min}
                                        max={monthBounds.max}
                                        value={customFromKey}
                                        onChange={(e) => handleCustomFromChange(e.target.value)}
                                        className="text-sm border border-gray-300 rounded-lg px-3 py-2.5 bg-white text-gray-800 focus:border-[#1e3a8a] outline-none cursor-pointer w-full"
                                    />
                                </div>
                                <div className="flex flex-col gap-1 flex-1 min-w-[160px]">
                                    <label htmlFor="emp-slip-to-month" className="text-[10px] font-semibold text-gray-600">
                                        To (end month)
                                    </label>
                                    <input
                                        id="emp-slip-to-month"
                                        type="month"
                                        min={monthBounds.min}
                                        max={monthBounds.max}
                                        value={customToKey}
                                        onChange={(e) => handleCustomToChange(e.target.value)}
                                        className="text-sm border border-gray-300 rounded-lg px-3 py-2.5 bg-white text-gray-800 focus:border-[#1e3a8a] outline-none cursor-pointer w-full"
                                    />
                                </div>
                                <p className="hidden sm:flex items-end text-[10px] text-gray-500 pb-2 flex-1">
                                    {slipCount} slip{slipCount !== 1 ? "s" : ""} · {formatPeriodRangeLabel(periods)}
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="pt-1 border-t border-gray-200/80">
                        <div className="flex items-center gap-2 mb-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                            <History size={12} /> Saved History
                        </div>
                        {historyLoading ? (
                            <p className="text-xs text-gray-400">Loading saved salary slips...</p>
                        ) : savedSlips.length === 0 ? (
                            <p className="text-xs text-gray-400">No saved salary slips yet. Download once to save this month.</p>
                        ) : (
                            <div className="flex gap-2 overflow-x-auto pb-1">
                                {savedSlips.map((slip) => (
                                    <button
                                        key={slip._id || slip.periodKey}
                                        type="button"
                                        onClick={() => loadSavedSlip(slip)}
                                        className="shrink-0 rounded-lg border border-gray-300 bg-white px-3 py-2 text-left hover:border-[#1e3a8a] transition-all"
                                    >
                                        <span className="flex items-center gap-1 text-xs font-bold text-gray-800">
                                            {slip.locked !== false ? <Lock size={11} /> : <Unlock size={11} />}
                                            {slip.payMonth}
                                        </span>
                                        <span className="block text-[10px] text-gray-400">
                                            Saved {new Date(slip.updatedAt || slip.createdAt).toLocaleDateString("en-IN")}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                        {payslipIssues.length > 0 && (
                            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                                <strong className="block mb-1">Payslip issue history</strong>
                                {payslipIssues.slice(0, 3).map((issue) => (
                                    <p key={issue._id} className="mt-1">
                                        {issue.payMonth || issue.periodKey || "Payslip"} · {issue.issueType} · {issue.status}
                                        {issue.adminComment ? ` · ${issue.adminComment}` : ""}
                                    </p>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="overflow-auto bg-white p-4 max-h-[70vh]">
                    <div ref={printAreaRef}>
                        {displaySlipsData.map(({ period, expenses, employee: savedEmployee }, idx) => (
                            <SalarySlipDocument companySettings={companySettings?.salarySlip}
                                key={period.key}
                                employee={savedEmployee || employee}
                                period={period}
                                approvedExpenses={expenses}
                                signatureSrc={signatureSrc}
                                pageBreakAfter={idx < slipsData.length - 1}
                            />
                        ))}
                    </div>
                </div>
                <div className="flex justify-end gap-2 border-t border-gray-200 bg-gray-50 px-5 py-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-600 transition-all hover:bg-gray-100"
                    >
                        Close
                    </button>
                </div>
            </div>
            {issueOpen && (
                <div className="fixed inset-0 z-[260] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/50" onClick={() => setIssueOpen(false)} />
                    <form onSubmit={submitPayslipIssue} className="relative w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <p className="text-[10px] font-bold uppercase tracking-widest text-amber-600">Payslip issue</p>
                                <h3 className="text-xl font-black text-gray-900">Report salary, bank, or LOP mismatch</h3>
                            </div>
                            <button type="button" onClick={() => setIssueOpen(false)} className="h-9 w-9 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center">
                                <X size={17} />
                            </button>
                        </div>
                        <div className="mt-4 grid gap-3">
                            <label className="grid gap-1 text-sm font-bold text-gray-700">
                                Issue type
                                <select
                                    value={issueForm.issueType}
                                    onChange={(event) => setIssueForm((current) => ({ ...current, issueType: event.target.value }))}
                                    className="rounded-xl border border-gray-300 px-3 py-2 text-sm"
                                >
                                    <option value="salary">Salary mismatch</option>
                                    <option value="bank">Bank detail mismatch</option>
                                    <option value="lop">LOP mismatch</option>
                                    <option value="expense">Expense mismatch</option>
                                    <option value="other">Other</option>
                                </select>
                            </label>
                            <label className="grid gap-1 text-sm font-bold text-gray-700">
                                Details
                                <textarea
                                    rows={4}
                                    value={issueForm.description}
                                    onChange={(event) => setIssueForm((current) => ({ ...current, description: event.target.value }))}
                                    placeholder={`Explain the mismatch in at least ${PAYSLIP_ISSUE_MIN_WORDS} words...`}
                                    className="rounded-xl border border-gray-300 px-3 py-2 text-sm"
                                />
                            </label>
                            {issueMessage && <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-600">{issueMessage}</p>}
                            <button type="submit" disabled={issueSaving} className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#1e3a8a] px-4 py-2 text-sm font-bold text-white disabled:opacity-60">
                                <Send size={16} />
                                {issueSaving ? "Submitting..." : "Submit Issue"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default SalarySlipModal;
